// Whiteboard engine — the board in the browser: its elements, what is still
// to be saved, undo / redo, and other people's changes arriving live.
// Plain JS (subscribe / emit) so React, the saver and the live room all share
// ONE board; the suite drives it in Node.
//
//   commit(next[])     my change: each element stamped (version + 1), kept in
//                      `pending` for the saver, and undoable as one step
//   applyRemote(list)  someone else's (or the server's) elements: each one
//                      replaces mine only if it wins (scene.js `wins`)
//   preview            a gesture in progress (drag, resize, typing): drawn,
//                      never saved, until commit

import { stamp, wins, byZ } from './scene.js';

const HISTORY_LIMIT = 200;

export class BoardModel {
  constructor() {
    this.els = new Map();
    this.dead = new Map();        // id → { version, nonce } of deleted elements
    this.pending = new Map();     // id → element (or tombstone) waiting for the saver
    this.inflight = new Map();    // id → what the saver has sent and the server has not yet answered
    this.preview = null;          // Map id → element | null while a gesture runs
    this.undoStack = [];
    this.redoStack = [];
    this.listeners = new Set();
    this.rev = 0;
    this._ordered = null;
  }

  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  emit() { this.rev += 1; this._ordered = null; for (const fn of this.listeners) fn(); }

  get(id) {
    if (this.preview && this.preview.has(id)) return this.preview.get(id) || null;
    return this.els.get(id) || null;
  }
  // The live elements in drawing order (a gesture's preview included).
  ordered() {
    if (this._ordered) return this._ordered;
    let list;
    if (this.preview) {
      const m = new Map(this.els);
      for (const [id, el] of this.preview) { if (el) m.set(id, el); else m.delete(id); }
      list = [...m.values()];
    } else list = [...this.els.values()];
    list.sort(byZ);
    this._ordered = list;
    return list;
  }
  get size() { return this.els.size; }
  maxZ() { let z = 0; for (const el of this.els.values()) if (el.z > z) z = el.z; return z; }
  minZ() { let z = 0; for (const el of this.els.values()) if (el.z < z) z = el.z; return z; }

  // The board as the server holds it. My unsaved changes — and those on their
  // way to the server right now — stay on top.
  load(list) {
    const fresh = new Map();
    for (const el of list || []) if (el && !el.deleted) fresh.set(el.id, el);
    const lay = (mine) => { if (mine.deleted) fresh.delete(mine.id); else fresh.set(mine.id, mine); };
    for (const mine of this.inflight.values()) {
      const theirs = fresh.get(mine.id);
      if (!theirs || wins(mine, theirs)) lay(mine);
    }
    for (const [id, mine] of this.pending) {
      const theirs = fresh.get(id);
      if (theirs && !wins(mine, theirs)) { this.pending.delete(id); continue; }
      lay(mine);
    }
    this.els = fresh;
    this.emit();
  }

  setPreview(map) { this.preview = map && map.size ? map : null; this.emit(); }
  clearPreview() { if (this.preview) { this.preview = null; this.emit(); } }

  // Apply MY changes: next = full elements (new or changed) or { id, deleted: true }.
  commit(next, { history = true } = {}) {
    const before = []; const after = [];
    for (const n of next) {
      const cur = this.els.get(n.id) || null;
      const deadMeta = this.dead.get(n.id);
      if (n.deleted) {
        if (!cur) continue;
        const tomb = { id: n.id, version: cur.version + 1, nonce: Math.floor(Math.random() * 2 ** 31), updated: Date.now(), deleted: true };
        this.els.delete(n.id);
        this.dead.set(n.id, { version: tomb.version, nonce: tomb.nonce });
        this.pending.set(n.id, tomb);
        before.push(cur); after.push(null);
      } else {
        const el = stamp(n, cur || deadMeta || n);
        this.els.set(el.id, el);
        this.dead.delete(el.id);
        this.pending.set(el.id, el);
        before.push(cur); after.push(el);
      }
    }
    this.preview = null;
    if (history && after.length) {
      this.undoStack.push({ before, after });
      if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
      this.redoStack = [];
    }
    this.emit();
    return after;
  }

  canUndo() { return this.undoStack.length > 0; }
  canRedo() { return this.redoStack.length > 0; }
  undo() { return this._step(this.undoStack, this.redoStack, 'before', 'after'); }
  redo() { return this._step(this.redoStack, this.undoStack, 'after', 'before'); }
  // Put back one side of a step (its CONTENTS — the versions move on), and
  // hand the step to the other stack as it is.
  _step(from, to, want, had) {
    const entry = from.pop();
    if (!entry) return null;
    const next = [];
    entry[want].forEach((target, i) => {
      const id = (target || entry[had][i]).id;
      if (target) next.push({ ...target });
      else if (this.els.has(id)) next.push({ id, deleted: true });
    });
    this.commit(next, { history: false });
    to.push(entry);
    return entry[want].filter(Boolean).map((t) => t.id);
  }

  // Someone else's elements (live) or the server's answer to a save.
  applyRemote(list) {
    let changed = false;
    for (const inc of list || []) {
      if (!inc || !inc.id) continue;
      const cur = this.els.get(inc.id) || this.dead.get(inc.id) || null;
      if (!wins(inc, cur)) continue;
      const mine = this.pending.get(inc.id);
      if (mine && !wins(inc, mine)) continue;
      if (mine) this.pending.delete(inc.id);
      if (inc.deleted) {
        if (this.els.delete(inc.id)) changed = true;
        this.dead.set(inc.id, { version: inc.version, nonce: inc.nonce });
      } else {
        this.els.set(inc.id, inc);
        this.dead.delete(inc.id);
        changed = true;
      }
    }
    if (changed) this.emit();
    return changed;
  }

  // ---- the saver's side ------------------------------------------------------------
  // Hand out what is waiting (those `ready` allows: an image waits for its upload).
  takePending(ready = () => true) {
    const out = [];
    for (const [id, el] of this.pending) {
      if (!ready(el)) continue;
      out.push(el);
      this.pending.delete(id);
      this.inflight.set(id, el);
    }
    return out;
  }
  // The server has them.
  settle(list) {
    for (const el of list) if (this.inflight.get(el.id) === el) this.inflight.delete(el.id);
  }
  // A save failed: put back what no newer change has replaced since.
  restorePending(list) {
    for (const el of list) {
      if (this.inflight.get(el.id) === el) this.inflight.delete(el.id);
      const now = this.pending.get(el.id);
      if (!now || !wins(now, el)) this.pending.set(el.id, el);
    }
  }
  hasPending() { return this.pending.size > 0; }
}
